import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiUploadCloud } from "react-icons/fi";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { ThreeDot } from "react-loading-indicators";
import { GetInputsBySubCategoryIdData, GetPackagesBySubCategoryIdData } from "../../lib/data";
import { updateAdsForm } from "../../lib/actions";
import DropDownFilterPaginations from "../../components/GDropDownList/DropDownFilterPaginations";
import DropDown from "../../components/GDropDownList/DropDown";
import toast from "react-hot-toast";

export default function EditAdsUi({ adData, adId, adsInfoData, userData, typeOfcurrency, search }) {
    const nav = useNavigate();
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n);
    const currentLang = i18n.language;

    const [loading, setLoading] = useState(false);
    
    // States for Main Photo and Additional Photos
    const [mainImage, setMainImage] = useState(adData?.main_photo ? { url: adData.main_photo } : null);
    
    // We explicitly keep the real `id` for existing photos so we can send it back to the server
    const [selectedImages, setSelectedImages] = useState(
        adData?.photos?.map((p, i) => ({ 
            id: p.id, // Keep the real Database ID
            url: p.photo, 
            isExisting: true 
        })) || []
    );

    // Categories state
    const categories = adsInfoData?.categories || [];
    const [selectedCategory, setSelectedCategory] = useState(adData?.category_id || null);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState(adData?.sub_category_id || null);

    // Dynamic data
    const [dynamicInputs, setDynamicInputs] = useState([]);
    const [dynamicPackages, setDynamicPackages] = useState([]);
    
    // Package State: If package exists but package_id is null, it's a Custom Package ("")
    const initialPackageId = adData?.package?.package_id === null && adData?.package 
        ? "" 
        : (adData?.package?.package_id || adData?.package_id || null);
        
    const [selectedPackageId, setSelectedPackageId] = useState(initialPackageId);
    
    // Other selections
    const [selectedGovernorate, setSelectedGovernorate] = useState(adData?.governorate_id || null);
    const [selectedType, setSelectedType] = useState(adData?.ad_type_id || adData?.type_id || null);
    const [selectedStatus, setSelectedStatus] = useState(adData?.ad_status_id || adData?.status_id || adData?.order || null);
    const [selectedUser, setSelectedUser] = useState(adData?.user_id);
    const [selectedCurrency, setSelectedCurrency] = useState(adData?.currency || null);

    // Initial load for SubCategories and Dynamic Inputs
    useEffect(() => {
        if (selectedCategory) {
            const category = categories.find(c => String(c.id) === String(selectedCategory));
            if (category && category.children) {
                setSubCategories(category.children);
            }
        }
        if (selectedSubCategory) {
            handleSubCategoryChange(selectedSubCategory, true);
        }
        if (adData?.user_id) {
            search(adData?.name);
        }
    }, []);

    // Cleanup object URLs to prevent memory leaks when component unmounts
    useEffect(() => {
        return () => {
            if (mainImage?.file && mainImage?.url) URL.revokeObjectURL(mainImage.url);
            selectedImages.forEach(img => {
                if (img.file) URL.revokeObjectURL(img.url);
            });
        };
    }, [mainImage, selectedImages]);

    const handleSearch = (term) => {
        setSelectedUser(term);
        search(term);
    };

    // Handle Category change
    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        setSelectedSubCategory(null);
        setDynamicInputs([]);
        setDynamicPackages([]);
        
        const category = categories.find(c => String(c.id) === String(categoryId));

        if (category && category.children) {
            setSubCategories(category.children);
        } else {
            setSubCategories([]);
        }
    };

    // Handle SubCategory change
    const handleSubCategoryChange = async (subCategoryId, isInitialLoad = false) => {
        if (!isInitialLoad) {
            setSelectedSubCategory(subCategoryId);
        }
        if (subCategoryId) {
            setLoading(true);
            try {
                const [inputsData, packagesData] = await Promise.all([
                    GetInputsBySubCategoryIdData(subCategoryId),
                    GetPackagesBySubCategoryIdData(subCategoryId)
                ]);
                setDynamicInputs(inputsData || []);
                setDynamicPackages(packagesData || []);
            } catch (error) {
                console.error("Error fetching dynamic data:", error);
            } finally {
                setLoading(false);
            }
        } else {
            setDynamicInputs([]);
            setDynamicPackages([]);
        }
    };

    const handleMainImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (mainImage?.file && mainImage?.url) URL.revokeObjectURL(mainImage.url);
            setMainImage({ file: file, url: URL.createObjectURL(file) });
        }
    };

    const handleDeleteMainImage = () => {
        if (mainImage?.file && mainImage?.url) URL.revokeObjectURL(mainImage.url);
        setMainImage(null);
    };

    const handleAdditionalImagesChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files);
            const newImages = filesArray.map((file, index) => ({
                id: Date.now() + index,
                file: file,
                url: URL.createObjectURL(file),
                isExisting: false // Mark as a brand new photo
            }));
            setSelectedImages(prev => [...prev, ...newImages]);
        }
        e.target.value = null; 
    };

    const handleDeleteAdditionalImage = (idToRemove) => {
        setSelectedImages(prev => prev.filter(img => {
            if (img.id === idToRemove) {
                if (img.file) URL.revokeObjectURL(img.url);
                return false;
            }
            return true;
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!mainImage) {
            toast.error(currentLang === "en" ? "Please upload a main photo." : "الرجاء رفع الصورة الرئيسية.");
            return;
        }

        setLoading(true);
        const formData = new FormData(e.target);
        
        if (selectedCategory) formData.append('category_id', selectedCategory);
        if (selectedSubCategory) formData.append('sub_category_id', selectedSubCategory);
        if (selectedGovernorate) formData.append('governorate_id', selectedGovernorate);
        if (selectedType) formData.append('ad_type_id', selectedType);
        if (selectedStatus) formData.append('ad_status_id', selectedStatus);

        if (mainImage.file) {
            formData.append('main_photo', mainImage.file);
        }

        // ==========================================
        // PHOTO SORTING LOGIC ADDED HERE
        // ==========================================
        let existingIndex = 0;
        let newIndex = 0;

        selectedImages.forEach(img => {
            if (img.isExisting && img.id) {
                // It's an old photo retained from the database
                formData.append(`existing_photos_ids[${existingIndex}]`, img.id);
                existingIndex++;
            } else if (img.file) {
                // It's a completely new photo file
                formData.append(`photos[${newIndex}]`, img.file);
                newIndex++;
            }
        });
        // ==========================================

        try {
            const result = await updateAdsForm(adId, formData);
            if(result) {
                nav('/ads');
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error(currentLang === "en" ? "Failed to update ad" : "فشل تعديل الإعلان");
        } finally {
            setLoading(false);
        }
    };

    const renderDynamicInput = (input, index) => {
        const idName = `inputs[${index}][id]`;
        const valueName = `inputs[${index}][value]`;
        
        const existingInput = adData?.inputs?.find(item => String(item.id) === String(input.id));
        const existingValue = existingInput ? existingInput.value : "";
        
        return (
            <div key={input.id} className="col-span-1">
                <label className="block text-white mb-2">
                    {input.name} {input.required && <span className="text-red-500">*</span>}
                </label>
                
                <input type="hidden" name={idName} value={input.id} />
                
                {input.type === 'select' && input.options ? (
                    <select
                        name={valueName}
                        required={input.required}
                        defaultValue={existingValue}
                        className="w-full px-4 py-1 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                    >
                        <option value="">{currentLang === "en" ? "Select" : "اختر"}</option>
                        {input.options.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.option}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={input.type === 'number' ? 'number' : 'text'}
                        name={valueName}
                        required={input.required}
                        defaultValue={existingValue}
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                        placeholder={input.name}
                    />
                )}
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit} className="pb-10 relative">
            {loading && (
                <div className="absolute inset-0 bg-black/50 z-50 flex justify-center items-center rounded-lg">
                    <ThreeDot variant="bounce" color="#D06421" size="large" text="" textColor="" />
                </div>
            )}

            {/* --- PHOTO SECTIONS --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="col-span-1">
                    <label className="block text-white mb-2">
                        {currentLang === "en" ? "Main Photo" : "الصورة الرئيسية"} 
                        <span className="text-red-500">*</span>
                    </label>
                    <div className="bg-thirdly p-4 rounded-2xl border-2 border-dashed border-gray-500/50 hover:border-secondary transition-colors h-64 flex flex-col justify-center">
                        <div className="relative h-full flex justify-center items-center w-full">
                            {!mainImage ? (
                                <div className="text-center w-full h-full flex flex-col items-center justify-center relative cursor-pointer group">
                                    <FiUploadCloud className="w-10 h-10 text-gray-400 mx-auto mb-2 group-hover:text-secondary transition-colors" />
                                    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                                        {currentLang === "en" ? "Click to upload main photo" : "انقر لرفع الصورة الرئيسية"}
                                    </span>
                                    <input type="file" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleMainImageChange} />
                                </div>
                            ) : (
                                <div className="relative w-full h-full flex justify-center group">
                                    <img src={mainImage.url.startsWith('http') || mainImage.url.startsWith('blob') ? mainImage.url : `/${mainImage.url}`} alt="Main Preview" className="h-full w-full object-cover rounded-lg border border-gray-600" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex justify-center items-center">
                                        <button type="button" className="bg-red-500 text-white p-3 rounded-full shadow-lg hover:bg-red-600 transition-colors" onClick={handleDeleteMainImage}>
                                            <MdOutlineDeleteOutline className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-span-1 lg:col-span-2 flex flex-col">
                    <label className="block text-white mb-2">
                        {currentLang === "en" ? "Additional Photos" : "صور إضافية"}
                    </label>
                    <div className="bg-thirdly p-4 rounded-2xl border-2 border-dashed border-gray-500/50 h-64 overflow-y-auto custom-scrollbar flex-1">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            <div className="relative h-28 flex justify-center items-center border-2 border-dashed border-gray-600 bg-gray-800/30 rounded-xl hover:border-secondary transition-colors group cursor-pointer">
                                <div className="text-center">
                                    <FiUploadCloud className="w-6 h-6 text-gray-400 mx-auto mb-1 group-hover:text-secondary transition-colors" />
                                    <span className="text-xs text-gray-300 group-hover:text-white transition-colors">
                                        {currentLang === "en" ? "Add more" : "إضافة المزيد"}
                                    </span>
                                </div>
                                <input type="file" multiple accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleAdditionalImagesChange} />
                            </div>

                            {selectedImages.map((img) => (
                                <div key={img.id} className="relative h-28 rounded-xl overflow-hidden border border-gray-600 group">
                                    <img src={img.url.startsWith('http') || img.url.startsWith('blob') ? img.url : `/${img.url}`} alt="Preview" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center items-center">
                                        <button 
                                            type="button" 
                                            onClick={() => handleDeleteAdditionalImage(img.id)}
                                            className="bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                                        >
                                            <MdOutlineDeleteOutline className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MAIN AD INFORMATION --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Title" : "العنوان"}</label>
                    <input type="text" name="title" defaultValue={adData?.title} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder={currentLang === "en" ? "Enter The Title" : "ادخل العنوان"} required />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Description" : "الوصف"}</label>
                    <input type="text" name="description" defaultValue={adData?.description} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder={currentLang === "en" ? "Enter The Description" : "ادخل الوصف"} required />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Price" : "السعر"}</label>
                    <input type="number" name="price" defaultValue={adData?.price} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" required />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Currency" : "العملة"}</label>
                    <DropDown
                        options={typeOfcurrency || []} 
                        value={selectedCurrency} 
                        onchange={setSelectedCurrency} 
                        defaultText={currentLang === "en" ? "Select Currency" : "اختر العملة"} 
                        paramkey="currency" 
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "User" : "المستخدم"}</label>
                    <DropDownFilterPaginations 
                        options={userData || []} 
                        value={selectedUser}
                        search={search} 
                        onchange={setSelectedUser} 
                        defaultText={adData?.name} 
                        paramkey="user_id" 
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Schedule At" : "جدولة النشر"}</label>
                    <input type="date" name="schedule_at" defaultValue={adData?.schedule_at?.split(' ')[0]} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Governorate" : "المحافظة"}</label>
                    <DropDownFilterPaginations 
                        options={adsInfoData?.Governorate || []} 
                        value={selectedGovernorate} 
                        onchange={setSelectedGovernorate} 
                        defaultText={currentLang === "en" ? "Select Governorate" : "اختر المحافظة"} 
                        paramkey="id" 
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Location" : "العنوان"}</label>
                    <input type="text" name="location" defaultValue={adData?.location} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder={currentLang === "en" ? "Enter The Location" : "ادخل العنوان"} required />
                </div>

                {/* <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Latitude" : "خط العرض"}</label>
                    <input type="text" name="latitude" defaultValue={adData?.latitude} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder="e.g., 33.5097" />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Longitude" : "خط الطول"}</label>
                    <input type="text" name="longitude" defaultValue={adData?.longitude} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder="e.g., 36.2765" />
                </div> */}

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Ad Type" : "نوع الإعلان"}</label>
                    <DropDownFilterPaginations 
                        options={adsInfoData?.AdsTypes || []} 
                        value={selectedType} 
                        onchange={setSelectedType} 
                        defaultText={currentLang === "en" ? "Select Ad Type" : "اختر نوع الإعلان"} 
                        paramkey="id" 
                        nameKey="type"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Ad Status" : "حالة الإعلان"}</label>
                    <DropDownFilterPaginations 
                        options={adsInfoData?.AdsStatus || []} 
                        value={selectedStatus} 
                        onchange={setSelectedStatus} 
                        defaultText={currentLang === "en" ? "Select Ad Status" : "اختر حالة الإعلان"} 
                        paramkey="id" 
                        nameKey="status"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Category" : "الفئة"}</label>
                    <DropDownFilterPaginations 
                        options={categories} 
                        value={selectedCategory} 
                        onchange={handleCategoryChange} 
                        defaultText={currentLang === "en" ? "Select Category" : "اختر الفئة"} 
                        paramkey="id" 
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Sub Category" : "الفئة الفرعية"}</label>
                    <DropDownFilterPaginations 
                        options={subCategories} 
                        value={selectedSubCategory} 
                        onchange={handleSubCategoryChange} 
                        defaultText={currentLang === "en" ? "Select Sub Category" : "اختر الفئة الفرعية"} 
                        paramkey="id" 
                    />
                </div>
            </div>

            {/* --- DYNAMIC AD DETAILS --- */}
            {dynamicInputs.length > 0 && (
                <div className="mt-8 border-t border-gray-700 pt-6">
                    <h3 className="text-white text-xl font-semibold mb-4">
                        {currentLang === "en" ? "Ad Details" : "تفاصيل الإعلان"}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {dynamicInputs.map((input, index) => renderDynamicInput(input, index))}
                    </div>
                </div>
            )}

            {/* --- PACKAGES --- */}
            {dynamicPackages.length > 0 && (
                <div className="mt-8 border-t border-gray-700 pt-8">
                    <h3 className="text-white text-xl font-semibold mb-6">
                        {currentLang === "en" ? "Select a Package" : "اختر الباقة المناسبة"}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {dynamicPackages.map(pkg => {
                            const packageTitle = currentLang === "en" ? pkg.title?.en : pkg.title?.ar;
                            const isFree = parseFloat(pkg.price) === 0;
                            const formattedPrice = isFree ? (currentLang === "en" ? "Free" : "مجاناً") : `${pkg.price} JOD`; 
                            const isHomePosition = String(pkg.position) === "2";
                            const isVipShape = String(pkg.ad_shape) === "2";
                            const isPrimaryTopResult = pkg.topResult === true || String(pkg.topResult) === "1";

                            const positionText = isHomePosition ? t("positions.home") : t("positions.normal");
                            const shapeText = isVipShape ? t("positions.vip_shape") : t("positions.normal_shape");
                            const topResultText = isPrimaryTopResult ? t("topResults.primary") : t("topResults.secondary");

                            return (
                                <label key={pkg.id} className="relative cursor-pointer group">
                                    <input 
                                        type="radio" 
                                        name="package_id" 
                                        value={pkg.id} 
                                        checked={String(selectedPackageId) === String(pkg.id)} 
                                        className="hidden peer" 
                                        required 
                                        onChange={(e) => setSelectedPackageId(e.target.value)} 
                                    />
                                    
                                    <div className={`h-full flex flex-col p-6 rounded-2xl border-2 transition-all duration-300 overflow-hidden relative shadow-lg
                                        ${isVipShape || isPrimaryTopResult 
                                            ? 'border-gray-600 bg-thirdly peer-checked:border-yellow-500 peer-checked:bg-yellow-500/10 hover:border-yellow-500/50' 
                                            : 'border-gray-700 bg-thirdly peer-checked:border-secondary peer-checked:bg-secondary/10 hover:border-gray-500'}`}
                                    >
                                        
                                        {(isPrimaryTopResult || isVipShape) && (
                                            <div className={`absolute top-0 right-0 text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg shadow-md z-10 ${isVipShape ? 'bg-yellow-600' : 'bg-secondary'}`}>
                                                {isPrimaryTopResult ? topResultText : shapeText}
                                            </div>
                                        )}

                                        <div className="text-center border-b border-gray-700/50 pb-5 mb-5">
                                            <h4 className="text-white font-bold text-xl mb-2">{packageTitle}</h4>
                                            <div className={`text-3xl font-extrabold ${isVipShape ? 'text-yellow-500' : 'text-secondary'}`}>
                                                {formattedPrice}
                                            </div>
                                        </div>

                                        <div className="flex-1 flex flex-col gap-4 text-sm text-gray-300">
                                            <div className="flex items-center gap-3">
                                                <svg className={`w-5 h-5 shrink-0 ${isVipShape ? 'text-yellow-500' : 'text-secondary'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                </svg>
                                                <span>
                                                    {currentLang === "en" ? "Duration:" : "المدة:"}{" "}
                                                    <strong className="text-white ml-1">
                                                        {pkg.duration} {currentLang === "en" ? "Days" : "يوم"}
                                                    </strong>
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <svg className={`w-5 h-5 shrink-0 ${isVipShape ? 'text-yellow-500' : 'text-secondary'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                </svg>
                                                <span>
                                                    {currentLang === "en" ? "Photos allowed:" : "الصور المسموحة:"}{" "}
                                                    <strong className="text-white ml-1">
                                                        {pkg.number_of_photo}
                                                    </strong>
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <svg className={`w-5 h-5 shrink-0 ${isVipShape ? 'text-yellow-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                                                </svg>
                                                <span>
                                                    {currentLang === "en" ? "Shape:" : "الشكل:"}{" "}
                                                    <strong className={isVipShape ? 'text-yellow-400 ml-1' : 'text-white ml-1'}>
                                                        {shapeText}
                                                    </strong>
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <svg className={`w-5 h-5 shrink-0 ${isHomePosition ? 'text-secondary' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                                                </svg>
                                                <span>
                                                    {currentLang === "en" ? "Position:" : "المكان:"}{" "}
                                                    <strong className="text-white ml-1">
                                                        {positionText}
                                                    </strong>
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <svg className={`w-5 h-5 shrink-0 ${isPrimaryTopResult ? 'text-secondary' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                                                </svg>
                                                <span>
                                                    {currentLang === "en" ? "Result Type:" : "نوع النتيجة:"}{" "}
                                                    <strong className="text-white ml-1">
                                                        {topResultText}
                                                    </strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-6 flex justify-center items-center">
                                            <div className={`w-6 h-6 rounded-full border-2 group-hover:border-gray-400 flex items-center justify-center transition-all duration-300
                                                ${isVipShape ? 'border-gray-500 peer-checked:border-yellow-500' : 'border-gray-500 peer-checked:border-secondary'}`}>
                                                <div className={`w-3 h-3 rounded-full scale-0 peer-checked:scale-100 transition-transform duration-300 ${isVipShape ? 'bg-yellow-500' : 'bg-secondary'}`}></div>
                                            </div>
                                        </div>
                                        
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                    
                    {/* Manual Custom Package Button */}
                    <label className="relative cursor-pointer group mt-6 block">
                        <input 
                            type="radio" 
                            name="package_id" 
                            value="" 
                            checked={String(selectedPackageId) === ""}
                            className="hidden peer" 
                            required 
                            onChange={(e) => setSelectedPackageId(e.target.value)} 
                        />
                        <div className="p-6 rounded-2xl border-2 transition-all duration-300 relative shadow-lg border-gray-700 bg-thirdly peer-checked:border-secondary peer-checked:bg-secondary/10 hover:border-gray-500 text-center">
                            <h4 className="text-white font-bold text-xl">{currentLang === "en" ? "Custom Package" : "باقة مخصصة"}</h4>
                            <p className="text-gray-400 mt-2">{currentLang === "en" ? "Click here to define your own package parameters" : "انقر هنا لتحديد معلمات الباقة الخاصة بك"}</p>
                        </div>
                    </label>

                    {/* Custom Package Dropdowns */}
                    {String(selectedPackageId) === "" && (
                        <div className="mt-8 border-t border-gray-700 pt-6">
                            <h3 className="text-white text-xl font-semibold mb-4">
                                {currentLang === "en" ? "Custom Package Details" : "تفاصيل الباقة المخصصة"}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                
                                <div className="col-span-1">
                                    <label className="block text-white mb-2">{currentLang === "en" ? "Duration" : "المدة"}</label>
                                    <input type="number" name="duration" defaultValue={adData?.package?.duration} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder="30" required />
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-white mb-2">{currentLang === "en" ? "Position" : "المكان"}</label>
                                    <select name="position" defaultValue={adData?.package?.position} className="w-full px-4 py-1 bg-thirdly border border-gray-500/100 text-white rounded-lg" required>
                                        <option value="1">{currentLang === "en" ? "Normal" : "عادي"}</option>
                                        <option value="2">{currentLang === "en" ? "Home" : "الرئيسية"}</option>
                                    </select>
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-white mb-2">{currentLang === "en" ? "Ad Shape" : "شكل الإعلان"}</label>
                                    <select name="ad_shape" defaultValue={adData?.package?.ad_shape} className="w-full px-4 py-1 bg-thirdly border border-gray-500/100 text-white rounded-lg" required>
                                        <option value="1">{currentLang === "en" ? "Normal Shape" : "شكل عادي"}</option>
                                        <option value="2">{currentLang === "en" ? "VIP Shape" : "شكل VIP"}</option>
                                    </select>
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-white mb-2">{currentLang === "en" ? "Top Result" : "النتيجة الأولى"}</label>
                                    <select name="topResult" defaultValue={adData?.package?.topResult ? "1" : "0"} className="w-full px-4 py-1 bg-thirdly border border-gray-500/100 text-white rounded-lg" required>
                                        <option value="0">{currentLang === "en" ? "Secondary (No)" : "ثانوية (لا)"}</option>
                                        <option value="1">{currentLang === "en" ? "Primary (Yes)" : "أساسية (نعم)"}</option>
                                    </select>
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-white mb-2">{currentLang === "en" ? "Order Price" : "سعر الطلب"}</label>
                                    <input type="number" name="order_price" defaultValue={adData?.package?.order_price ? parseFloat(adData.package.order_price) : ""} className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg" placeholder="25" required />
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-center w-full mt-8">
                <button type="submit" disabled={loading} className={`w-full md:w-1/3 bg-secondary text-white py-3 rounded-xl font-semibold shadow-lg transition hover:bg-orange-600 ${loading ? "cursor-not-allowed opacity-70" : "" }`} >
                    {loading ? (
                        <div className="flex items-center justify-center gap-2">
                             <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                            </svg>
                            {currentLang === "en" ? "Saving..." : "جاري الحفظ..."}
                        </div>
                    ) : 
                        t("button.save", "Save")
                    }
                </button>
            </div>
        </form>
    );
}